import React, {createRef, PureComponent, ChangeEvent, FormEvent} from 'react'
import {
  AlignItems,
  Appearance,
  Bullet,
  ButtonType,
  Columns,
  ComponentColor,
  ComponentSize,
  ComponentStatus,
  CTAButton,
  FlexBox,
  FlexDirection,
  Form,
  Grid,
  Heading,
  HeadingElement,
  Input,
  JustifyContent,
  Panel,
  Popover,
  PopoverInteraction,
  PopoverPosition,
  VisibilityInput,
} from '@influxdata/clockface'
import auth0js from 'auth0-js'
import LegalCheckbox from 'js/components/SignUp/LegalCheckbox'
import CSRF from 'js/components/CSRF'

// Constants
import {GTM_INVITE_ACCEPTED} from 'js/constants'
import FunnelHeader from '../shared/FunnelHeader'
import FunnelSubheader from '../shared/FunnelSubheader'

const {auth0: auth0Config} = window.Config

interface State {
  buttonStatus: ComponentStatus
  confirmPassword: string
  confirmPasswordError: string
  firstName: string
  firstNameError: string
  isPasswordVisible: boolean
  lastName: string
  lastNameError: string
  password: string
  passwordError: string
  passwordReqsVisible: boolean
  serviceAgreement: boolean
  serviceAgreementError: boolean
}

interface Props {
  email: string
  orgName: string
  token: string
}

class InviteSignUpForm extends PureComponent<Props> {
  public state: State = {
    buttonStatus: ComponentStatus.Default,
    confirmPassword: '',
    confirmPasswordError: '',
    firstName: '',
    firstNameError: '',
    isPasswordVisible: false,
    lastName: '',
    lastNameError: '',
    password: '',
    passwordError: '',
    passwordReqsVisible: false,
    serviceAgreement: false,
    serviceAgreementError: false,
  }

  public render() {
    const {
      buttonStatus,
      confirmPassword,
      confirmPasswordError,
      firstName,
      firstNameError,
      isPasswordVisible,
      lastName,
      lastNameError,
      password,
      passwordError,
      passwordReqsVisible,
      serviceAgreement,
      serviceAgreementError,
    } = this.state
    const {email, orgName} = this.props

    return (
      <form onSubmit={this.handleSubmit} className="sign-up--form">
        <FlexBox
          direction={FlexDirection.Column}
          justifyContent={JustifyContent.FlexStart}
          alignItems={AlignItems.Center}
          margin={ComponentSize.Large}
        >
          <FunnelHeader text={`You have been invited to join ${orgName}.`} />
          <FunnelSubheader
            text={`Complete the following to create your account for ${email} and join ${orgName}.`}
          />
          <FlexBox
            direction={FlexDirection.Column}
            alignItems={AlignItems.Center}
            margin={ComponentSize.Small}
          >
            <Panel style={{maxWidth: '720px'}}>
              <Panel.SymbolHeader
                symbol={<Bullet text={1} />}
                title={
                  <Heading element={HeadingElement.H3}>
                    Create your profile
                  </Heading>
                }
                size={ComponentSize.Medium}
              />
              <Panel.Body size={ComponentSize.Medium}>
                <Grid>
                  <Grid.Row>
                    <Grid.Column widthXS={Columns.Six}>
                      <Form.Element
                        label="First Name"
                        required={true}
                        errorMessage={firstNameError}
                      >
                        <Input
                          name="firstName"
                          value={firstName}
                          autoFocus={true}
                          size={ComponentSize.Medium}
                          onChange={this.handleInputChange}
                        />
                      </Form.Element>
                    </Grid.Column>
                    <Grid.Column widthXS={Columns.Six}>
                      <Form.Element
                        label="Last Name"
                        required={true}
                        errorMessage={lastNameError}
                      >
                        <Input
                          name="lastName"
                          value={lastName}
                          size={ComponentSize.Medium}
                          onChange={this.handleInputChange}
                        />
                      </Form.Element>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column widthXS={Columns.Twelve}>
                      <Form.Element
                        label="Password"
                        required={true}
                        errorMessage={passwordError}
                      >
                        <div ref={this.triggerRef}>
                          <VisibilityInput
                            name="password"
                            value={password}
                            size={ComponentSize.Medium}
                            onChange={this.handleInputChange}
                            onToggleClick={this.handleTogglePasswordVisibility}
                            visible={isPasswordVisible}
                            onFocus={() => this.togglePasswordReqs(true)}
                            onBlur={() => this.togglePasswordReqs(false)}
                          />
                        </div>
                        <Popover.Popover
                          triggerRef={this.triggerRef}
                          visible={passwordReqsVisible}
                          contents={() => (
                            <ul>
                              <li>Must be at least 8 characters.</li>
                              <li>Must not contain personal information.</li>
                              <li>
                                Must not be a common or previous password.
                              </li>
                            </ul>
                          )}
                          showEvent={PopoverInteraction.None}
                          hideEvent={PopoverInteraction.None}
                          position={PopoverPosition['ToTheRight']}
                          color={ComponentColor['Primary']}
                          appearance={Appearance['Outline']}
                        />
                      </Form.Element>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column widthXS={Columns.Twelve}>
                      <Form.Element
                        label="Confirm Password"
                        required={true}
                        errorMessage={confirmPasswordError}
                      >
                        <VisibilityInput
                          name="confirmPassword"
                          value={confirmPassword}
                          size={ComponentSize.Medium}
                          onChange={this.handleInputChange}
                          onToggleClick={this.handleTogglePasswordVisibility}
                          visible={isPasswordVisible}
                        />
                      </Form.Element>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <CSRF />
              </Panel.Body>
            </Panel>
            <Panel style={{maxWidth: '720px', width: '100%'}}>
              <Panel.SymbolHeader
                symbol={<Bullet text={2} />}
                title={
                  <Heading element={HeadingElement.H3}>
                    {' '}
                    Read and agree to our service agreement{' '}
                  </Heading>
                }
                size={ComponentSize.Medium}
              />
              <Panel.Body size={ComponentSize.Medium}>
                <Grid>
                  <Grid.Row>
                    <Grid.Column widthXS={Columns.Twelve}>
                      <LegalCheckbox
                        isError={serviceAgreementError}
                        isAgreeing={serviceAgreement}
                        onToggleAgree={this.onClickServiceAgreement}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Panel.Body>
            </Panel>
          </FlexBox>
          <CTAButton
            text="Join Organization"
            color={ComponentColor.Primary}
            type={ButtonType.Submit}
            status={buttonStatus}
          />
        </FlexBox>
      </form>
    )
  }

  private triggerRef = createRef<HTMLDivElement>()

  private get validFieldValues(): {
    isValid: boolean
    errors: {[fieldName: string]: string}
  } {
    const {firstName, lastName, password, confirmPassword} = this.state

    const firstNameError = !firstName && 'This field is required'
    const lastNameError = !lastName && 'This field is required'
    const passwordError = !password && 'This field is required'
    const confirmPasswordError =
      confirmPassword === password
        ? !confirmPassword && 'This field is required'
        : 'Passwords must match'

    const errors = {
      firstNameError,
      lastNameError,
      passwordError,
      confirmPasswordError,
    }

    const isValid = !Object.values(errors).some((error) => !!error)

    return {isValid, errors}
  }

  private handleTogglePasswordVisibility = () => {
    this.setState(({isPasswordVisible}: State) => ({
      isPasswordVisible: !isPasswordVisible,
    }))
  }

  private onClickServiceAgreement = () => {
    const {serviceAgreement} = this.state

    this.setState({
      serviceAgreement: !serviceAgreement,
    })
  }

  private handleSubmit = (e: FormEvent) => {
    const {isValid, errors} = this.validFieldValues
    const {
      password,
      firstName: given_name,
      lastName: family_name,
      serviceAgreement,
    } = this.state
    const {email, token} = this.props

    e.preventDefault()

    const auth0 = new auth0js.WebAuth({
      domain: auth0Config.domain,
      clientID: auth0Config.clientID,
      redirectUri: `${
        auth0Config.joinOrgRedirectURI
      }?token=${encodeURIComponent(token)}`,
      responseType: 'code',
    })

    if (!isValid) {
      this.setState(errors)
      return
    }

    if (!serviceAgreement) {
      this.setState({serviceAgreementError: true})
      return
    }

    this.setState({buttonStatus: ComponentStatus.Loading})

    auth0.signup(
      {
        connection: 'Username-Password-Authentication',
        email,
        password,
        family_name,
        given_name,
      } as any,
      (err) => {
        if (err) {
          this.displayErrorMessage(errors, err)
          this.setState({buttonStatus: ComponentStatus.Default})
          return
        }

        auth0.login(
          {
            realm: 'Username-Password-Authentication',
            email,
            password,
          },
          () => {}
        )

        // Google Tag Manager event for accepting invitation
        window.dataLayer.push({
          event: GTM_INVITE_ACCEPTED,
        })
      }
    )
  }

  private displayErrorMessage = (errors, auth0Err) => {
    if (/error in email/.test(auth0Err.code)) {
      this.setState({
        ...errors,
        emailError: 'Please enter a valid email address',
      })
    } else if (auth0Err.code === 'user_exists') {
      const emailError = `The user already exists. Try logging in instead.`
      this.setState({...errors, emailError})
    } else if (
      auth0Err.code === 'invalid_password' ||
      auth0Err.code === 'access_denied' ||
      auth0Err.code === 'extensibility_error'
    ) {
      switch (auth0Err.name) {
        case 'PasswordDictionaryError': {
          const passwordError = `Password is too common. Please choose another.`
          this.setState({...errors, passwordError})
          break
        }
        case 'PasswordNoUserInfoError': {
          const passwordError = `Password contains personal information. Please choose another.`
          this.setState({...errors, passwordError})
          break
        }
        default: {
          const passwordError = `Password is too short. Please choose another.`
          this.setState({...errors, passwordError})
          break
        }
      }
    } else {
      const emailError = `We have been notified of an issue while creating your account. If this issue persists, please contact support@influxdata.com`
      this.setState({...errors, emailError})
      throw new Error(auth0Err.description)
    }
  }

  private handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({[e.target.name]: e.target.value})
  }

  private togglePasswordReqs = (e: boolean) => {
    this.setState({passwordReqsVisible: e === true})
  }
}

export default InviteSignUpForm
